import Clarity from "@microsoft/clarity";
import {
	CLARITY_PROJECT_ID,
	IS_POC,
	IS_SELF_HOSTED,
} from "@/constants/constants";

export interface ClarityUser {
	/**
	 * Stable, opaque user identifier. Clarity SHA256-hashes this before it ever
	 * leaves the browser, so never pass raw PII here — use the Keycloak `sub`.
	 */
	id: string;
	/** Human-readable label shown in the Clarity dashboard. */
	friendlyName?: string;
}

export type ClarityTags = Record<string, string | string[] | undefined>;

let isInitialized = false;
let isInjected = false;

/**
 * Clarity is opt-in per environment: it only runs where the build supplies
 * `VITE_APP_CLARITY_PROJECT_ID`. Self-hosted and POC deployments are excluded
 * outright — they must not reach the public Clarity CDN.
 */
const isClarityEnabled = () =>
	Boolean(CLARITY_PROJECT_ID) && !IS_SELF_HOSTED && !IS_POC;

/**
 * Every Clarity method except `init` proxies straight to `window.clarity`,
 * which only exists once the tag has been injected. Guarding on the init flag
 * (and swallowing failures) keeps a disabled or blocked tag from throwing in
 * the middle of application code — analytics must never break the app.
 */
const withClarity = (action: () => void) => {
	if (!isInitialized) return;
	try {
		action();
	} catch {
		// Intentionally ignored: an ad-blocker or a CDN failure is not an app error.
	}
};

/**
 * Injects the Clarity tag and grants it analytics storage. Only the consent
 * service calls this, once the user has accepted the analytics category.
 * Repeat calls are no-ops; a call after `revokeClarityConsent` re-grants
 * consent on the already-injected tag instead of injecting it twice.
 */
const initClarity = () => {
	if (isInitialized || !isClarityEnabled()) return;

	try {
		if (!isInjected) {
			Clarity.init(CLARITY_PROJECT_ID);
			isInjected = true;
		}
		// No advertising use: ad storage stays denied even on full consent.
		Clarity.consentV2({ ad_Storage: "denied", analytics_Storage: "granted" });
		isInitialized = true;
	} catch {
		// Intentionally ignored: an ad-blocker or a CDN failure is not an app error.
	}
};

/**
 * Withdraws consent: Clarity erases its cookies and stops cookie-based
 * tracking. The injected tag itself cannot be unloaded, so the consent
 * service reloads the page afterwards.
 * See: https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-consent-api-v2
 */
const revokeClarityConsent = () => {
	withClarity(() => {
		Clarity.consent(false);
	});
	isInitialized = false;
};

/**
 * Ties the current session to a user so recordings can be found by person.
 * Call after authentication resolves.
 */
const identifyClarityUser = ({ id, friendlyName }: ClarityUser) =>
	withClarity(() => {
		Clarity.identify(id, undefined, undefined, friendlyName);
	});

/**
 * Attaches custom tags to the session. Tags are Clarity's filter/segmentation
 * dimension — tenant, environment and release are what make a recording
 * findable later. Empty values are skipped so filters stay clean.
 */
const setClarityTags = (tags: ClarityTags) =>
	withClarity(() => {
		for (const [key, value] of Object.entries(tags)) {
			if (value === undefined || value.length === 0) continue;
			Clarity.setTag(key, value);
		}
	});

/** Records a custom event, surfaced as a smart-event filter in Clarity. */
const trackClarityEvent = (eventName: string) =>
	withClarity(() => {
		Clarity.event(eventName);
	});

/**
 * Marks the session as high-priority so Clarity keeps the recording even when
 * sampling would otherwise drop it. Reserve this for genuinely interesting
 * sessions (crashes, failed flows) — upgrading everything defeats the purpose.
 */
const upgradeClaritySession = (reason: string) =>
	withClarity(() => {
		Clarity.upgrade(reason);
	});

export {
	identifyClarityUser,
	initClarity,
	isClarityEnabled,
	revokeClarityConsent,
	setClarityTags,
	trackClarityEvent,
	upgradeClaritySession,
};
